import { useState } from 'react';
import type { PlanListingResponse, SharingResponse } from './api/types';
import { useMessages } from './i18n/context';

/** The destination value that stands for "no parent" (the API's null parent id). */
export const TOP_LEVEL = '';

/**
 * The owner's move panel (ADR-027 §3/§4): re-parent the open plan under another plan the
 * caller owns, or un-nest it to the top level. Purely presentational over two server views —
 * the owner's aggregate view (its `parentId` is the current location) and the home listing
 * (every other plan the caller owns is a destination). The move itself round-trips upstairs,
 * and the server keeps the guards — Owner on the moved plan, ≥ Write on the destination, no
 * cycles, the depth cap — so a refusal surfaces in the alert banner rather than being
 * second-guessed here.
 */
export function MoveDialog({
  planId,
  name,
  sharing,
  rows,
  busy,
  onMove,
  onClose,
}: {
  planId: string;
  /** The moved plan's title. */
  name: string;
  /**
   * The owner's view (carries `parentId`), or null while it loads — a view of another plan
   * counts as loading too.
   */
  sharing: SharingResponse | null;
  /** The caller's home listing (the owned destinations), or null while it loads. */
  rows: readonly PlanListingResponse[] | null;
  busy: boolean;
  /** Called with the chosen parent id, or null for the top level. */
  onMove: (parentId: string | null) => void;
  onClose: () => void;
}) {
  const t = useMessages();
  const g = t.grove;
  // The destination the user picked; null until they touch the select (the current parent).
  const [choice, setChoice] = useState<string | null>(null);

  // A late answer for a previously open plan must not pose as this plan's location.
  if (sharing === null || sharing.id !== planId || rows === null) {
    return (
      <section className="dialog-panel" role="dialog" aria-labelledby="move-title">
        <div className="dialog-heading">
          <div>
            <p className="eyebrow">{g.organize}</p>
            <h3 id="move-title">{t.move.heading(name)}</h3>
          </div>
        </div>
        <p className="empty-state">{t.move.loading}</p>
        <div className="dialog-actions">
          <button className="button-secondary" disabled={busy} onClick={onClose}>
            {t.move.cancel}
          </button>
        </div>
      </section>
    );
  }

  const current = sharing.parentId ?? TOP_LEVEL;
  const selected = choice ?? current;
  const owned = rows.filter((row) => row.role === 'owner' && row.id !== planId);
  // The current parent may be a plan the caller only writes (a cross-owner move-in, ADR-027
  // §4) or one that has left their listing — keep it selectable so the select stays honest.
  const currentRow = rows.find((row) => row.id === current);
  const currentName = currentRow === undefined ? current : currentRow.name;
  const options =
    current !== TOP_LEVEL && !owned.some((row) => row.id === current)
      ? [{ id: current, name: currentName }, ...owned]
      : owned;

  return (
    <section className="dialog-panel" role="dialog" aria-labelledby="move-title">
      <div className="dialog-heading">
        <div>
          <p className="eyebrow">{g.organize}</p>
          <h3 id="move-title">{t.move.heading(name)}</h3>
        </div>
      </div>
      <p className="notice" aria-label={t.move.currentLocation}>
        {current === TOP_LEVEL ? t.move.atTopLevel : t.move.under(currentName)}
      </p>
      {owned.length === 0 && <p className="muted">{t.move.noOtherPlans}</p>}
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (selected !== current) onMove(selected === TOP_LEVEL ? null : selected);
        }}
      >
        <label className="field">
          {t.move.moveTo}{' '}
          <select
            aria-label={t.move.destination}
            value={selected}
            disabled={busy}
            onChange={(e) => setChoice(e.target.value)}
          >
            <option value={TOP_LEVEL}>{t.move.topLevel}</option>
            {options.map((option) => (
              <option key={option.id} value={option.id}>
                {option.name}
              </option>
            ))}
          </select>
        </label>
        <div className="dialog-actions">
          <button className="button-primary" type="submit" disabled={busy || selected === current}>
            {t.move.move}
          </button>
          <button className="button-secondary" type="button" disabled={busy} onClick={onClose}>
            {t.move.cancel}
          </button>
        </div>
      </form>
    </section>
  );
}
